import { Injectable } from '@angular/core';
import { BaseResponse, FormApiBaseService } from '@api/base-api';
import { notifyHeaders, RequestNotifyOptions } from '@core/constants';
import {
    RevokeSessionRequest,
    RevokeSessionResponse,
    SessionLogoutTransport
} from '@core/interfaces';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { EntityChangeResponse, NewEntityRequest, NewEntityResponse, PortalEntity } from '../interfaces/portal-entity.interface';

/**
 * Auth endpoints that are not tied to a specific user type.
 *
 * Implements `SessionLogoutTransport` so `SessionService` (in `libs/core`) can
 * reach the logout endpoint without `libs/core` importing `libs/api`.
 * `appConfig` (app.config.ts) binds this class to `SESSION_LOGOUT_TRANSPORT`.
 */
@Injectable()
export class AuthApiService extends FormApiBaseService implements SessionLogoutTransport {
    protected override scope = 'auth';
    protected override apiPath = 'api/heroes';

    /**
   * Revokes the session bound to `macAddress` and returns the caller's
   * remaining devices.
   *
   * No `retry(1)` here on purpose: the caller clears auth storage as soon as
   * this dispatches, so a retry would be sent without credentials and rejected.
   * POST api/heroes/logout
   */
    revokeDeviceSession(request: RevokeSessionRequest): Observable<RevokeSessionResponse> {
        return this.http
            .post<RevokeSessionResponse>(this.getFullUrl('logout'), request, this.getHttpOptions())
            .pipe(catchError(this.handleError));
    }

    /** Every partner this account works for. GET api/heroes/getAssociatedEntities */
    getAssociatedEntities(options: RequestNotifyOptions = {}): Observable<BaseResponse<PortalEntity[]>> {
        const query = this.getHttpOptionsForQuery();
        return this.http
            // `silent`: a caller that has nothing to tell the person when this fails (the switcher filling itself in quietly).
            .get<BaseResponse<PortalEntity[]>>(this.getFullUrl('getAssociatedEntities'), { headers: notifyHeaders(options, query.headers) })
            .pipe(retry(1), catchError(this.handleError));
    }

    /**
     * Registers another entity under the signed-in account (owner, 2026-10-31). It is created awaiting Lifemaan's approval.
     * POST api/heroes/membership/partners
     */
    createEntity(request: NewEntityRequest): Observable<BaseResponse<NewEntityResponse>> {
        return this.http
            .post<BaseResponse<NewEntityResponse>>(this.getFullUrl('membership/partners'), request, this.getHttpOptions())
            .pipe(catchError(this.handleError));
    }

    /** Signs this device in to more partners. POST api/heroes/addEntity */
    addEntities(entityIds: readonly string[]): Observable<EntityChangeResponse> {
        return this.http
            .post<EntityChangeResponse>(this.getFullUrl('addEntity'), { entityIds }, this.getHttpOptions())
            .pipe(catchError(this.handleError));
    }

    /** Signs this device out of some partners, leaving it signed in to the rest. POST api/heroes/removeEntity */
    removeEntities(entityIds: readonly string[]): Observable<EntityChangeResponse> {
        return this.http
            .post<EntityChangeResponse>(this.getFullUrl('removeEntity'), { entityIds }, this.getHttpOptions())
            .pipe(catchError(this.handleError));
    }
}
